import { ICellRendererParams } from "ag-grid-community";

// =======================================
// Option type for select cells
// =======================================
export interface OptionItem {
  value: string | number; // supports both string and number
  label: string;
}

// =======================================
// TimeSpan range definition
// =======================================
type TimeRange = {
  start: string; // Start time in "HH:mm:ss" format for seconds or "HH:mm" for minutes
  end: string;   // End time in same format as start
  step: number;  // Step size, in units defined by `unit` (seconds or minutes)
  displayHHmm?: boolean; // true = display "HH:mm", false = display total minutes or seconds
  unit?: "seconds" | "minutes"; // Unit of step
};

// =======================================
// Generic cell data type
// =======================================
type CellData =
  | { type: "boolean"; value: boolean }
  | { type: "select"; value: string | number; options: OptionItem[] }
  | { type: "text"; value: string | number }
  | {
      type: "timespan";
      value: string;       // "HH:mm:ss"
      range?: TimeRange;
    };

type SectionRow = {
  isSection: true;
  label: string;
  description?: string;
};

type ValueRow = CellData & {
  isSection?: false;
  disabled?: boolean;
};

type SettingRow = SectionRow | ValueRow;

// =======================================
// TimeSpan dropdown helpers
// =======================================
const generateTimeOptions = (
  start: string,
  end: string,
  step: number,
  unit: "minutes" | "seconds" = "minutes"
): string[] => {
  const result: string[] = [];

  const toSeconds = (t: string) => {
    const parts = t.split(":").map(Number);
    if (parts.length === 2) {
      const [h, m] = parts;
      return h * 3600 + m * 60;
    } else if (parts.length === 3) {
      const [h, m, s] = parts;
      return h * 3600 + m * 60 + s;
    } else {
      return 0;
    }
  };

  let currentSec = toSeconds(start);
  const endSec = toSeconds(end);

  while (currentSec <= endSec) {
    const h = Math.floor(currentSec / 3600);
    const m = Math.floor((currentSec % 3600) / 60);
    const s = currentSec % 60;
    result.push(`${h.toString().padStart(2, "0")}:${m
      .toString()
      .padStart(2, "0")}:${s.toString().padStart(2, "0")}`);
    currentSec += unit === "minutes" ? step * 60 : step;
  }

  return result;
};

// =======================================
// Generic DynamicCellRenderer
// =======================================
export function DynamicCellRenderer<T extends SettingRow>(
  props: ICellRendererParams<T>
) {
  const { data, value, colDef, node } = props;

  if (!data || !colDef?.field || data.isSection) return null;

  switch (data.type) {
    case "boolean":
      return (
        <input
          type="checkbox"
          checked={Boolean(value)}
          disabled={data.disabled}
          onChange={(e) =>
            node.setDataValue(colDef.field!, e.target.checked)
          }
        />
      );

    case "select":
      return (
        <select
          value={String(value)}
          disabled={data.disabled}
          onChange={(e) => {
            const selected = data.options.find(
              (opt) => String(opt.value) === e.target.value
            );
            if (selected) {
              // Set value with original type (number or string)
              node.setDataValue(colDef.field!, selected.value);
            }
          }}
        >
          {data.options.map((opt) => (
            <option key={String(opt.value)} value={String(opt.value)}>
              {opt.label}
            </option>
          ))}
        </select>
      );

      case "timespan": {
        const {
          start = "00:00",
          end = "23:59",
          step = 5,
          displayHHmm = true,
          unit = "minutes",
        } = data.range ?? {};

        const options = generateTimeOptions(start, end, step, unit);

        return (
          <select
            value={String(value)}
            disabled={data.disabled}
            onChange={(e) =>
              node.setDataValue(colDef.field!, e.target.value)
            }
            className="h-9 min-w-[80px] max-w-[200px] px-3 py-0.5 align-middle"
          >
            {options.map((t) => (
              <option key={t} value={t}>
                {displayHHmm
                  ? t.slice(0, 5)
                  : (() => {
                      const [h, m, s] = t.split(":").map(Number);
                      return unit === "minutes" ? h * 60 + m : h * 3600 + m * 60 + s;
                    })()}
              </option>
            ))}
          </select>
        );
      }

    case "text":
    default:
      return <span>{String(value)}</span>;
  }
}
